import React, { useState, useEffect } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import styles from "./ScheduleDemoDetailSty.module.css";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;
const API_BASE = `https://scheduledemo2.${backend_api_base_url}`;

export default function ScheduleDemoDetail() {
  const { demo_id } = useParams();
  const location = useLocation();
  const history = useHistory();

  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email");

  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newDemoTime, setNewDemoTime] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (!email) {
      setError("Email parameter is missing");
      setLoading(false);
      return;
    }

    const fetchDemo = async () => {
      try {
        setLoading(true);
        setError("");

        const url = new URL(`${API_BASE}/get`);
        url.searchParams.append("demo_reciever_email", email);
        url.searchParams.append("demo_id", demo_id);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        setDemo(data);

        if (data.demo_time) {
          const localDateTime = new Date(data.demo_time);
          localDateTime.setMinutes(
            localDateTime.getMinutes() - localDateTime.getTimezoneOffset()
          );
          setNewDemoTime(localDateTime.toISOString().slice(0, 16));
        }
      } catch (err) {
        console.error("Failed to fetch demo:", err);
        setError("Failed to load demo details");
      } finally {
        setLoading(false);
      }
    };

    fetchDemo();
  }, [email, demo_id]);

  const handleReschedule = async (e) => {
    e.preventDefault();

    if (!newDemoTime) {
      setUpdateError("Please select a valid date and time");
      return;
    }

    try {
      setUpdateLoading(true);
      setUpdateError("");
      setSuccessMessage("");

      const payload = {
        demo_reciever_email: demo.demo_reciever_email,
        demo_id: demo.demo_id,
        phone_no: demo.phone_no || "",   // ✅ include phone_no in update payload
        demo_time: newDemoTime,
      };

      const response = await fetch(`${API_BASE}/update`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Update failed: ${response.status} ${response.statusText}`
        );
      }

      setDemo({ ...demo, demo_time: newDemoTime });
      setSuccessMessage("Demo successfully rescheduled!");
    } catch (err) {
      console.error("Failed to reschedule demo:", err);
      setUpdateError("Failed to reschedule demo. Please try again.");
    } finally {
      setUpdateLoading(false);
    }
  };

  // ---- Inline Loader ----
  if (loading) {
    return (
      <div className={styles.demoWrap}>
        <div className={styles.demoDetailContainer}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              fontSize: "1rem",
              color: "#555",
              background: "#fafafa",
              borderRadius: "8px",
              border: "1px solid #eee",
            }}
          >
            Loading demo details...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.demoWrap}>
        <div className={styles.demoDetailContainer}>
          <h3>Error</h3>
          <p>{error}</p>
          <button className={styles.backButton} onClick={() => history.goBack()}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!demo) {
    return (
      <div className={styles.demoWrap}>
        <div className={styles.demoDetailContainer}>
          <h3>Demo Not Found</h3>
          <p>No demo found with ID: {demo_id}</p>
          <button className={styles.backButton} onClick={() => history.goBack()}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const assignedAgent =
    demo.assigned_agent && demo.assigned_agent.name
      ? `${demo.assigned_agent.name} (${demo.assigned_agent.uid})`
      : demo.assigned_to && demo.assigned_to !== "UNASSIGNED"
      ? demo.assigned_to
      : "Unassigned";

  return (
    <div className={styles.demoWrap}>
      <div className={styles.demoDetailContainer}>
        <h2>Demo Schedule Details</h2>

        <div className={styles.demoInfo}>
          <div className={styles.infoRow}>
            <span className={styles.label}>Receiver Email:</span>
            <span className={styles.value}>{demo.demo_reciever_email}</span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.label}>Phone Number:</span>
            <span className={styles.value}>
              {demo.phone_no || "Not provided"}
            </span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.label}>Demo ID:</span>
            <span className={styles.value}>{demo.demo_id}</span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.label}>Assigned To:</span>
            <span
              className={`${styles.value} ${
                assignedAgent === "Unassigned" ? styles.unassigned : ""
              }`}
            >
              {assignedAgent}
            </span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.label}>Current Scheduled Time:</span>
            <span className={styles.value}>
              {demo.demo_time
                ? new Date(demo.demo_time).toLocaleString()
                : "Not scheduled"}
            </span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.label}>Product:</span>
            <span className={styles.value}>
              {demo.product || "Not specified"}
            </span>
          </div>

          <div className={styles.infoRow}>
            <span className={styles.label}>Status:</span>
            <span className={styles.value}>{demo.status || "PENDING"}</span>
          </div>

          {demo.demo_url && (
            <div className={styles.infoRow}>
              <span className={styles.label}>Demo URL:</span>
              <a
                href={demo.demo_url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.demoLink}
              >
                Join Demo
              </a>
            </div>
          )}
        </div>

        <div className={styles.rescheduleSection}>
          <h3>Reschedule Demo</h3>

          {updateError && <p className={styles.error}>{updateError}</p>}
          {successMessage && <p className={styles.success}>{successMessage}</p>}

          <form onSubmit={handleReschedule}>
            <div className={styles.formGroup}>
              <label htmlFor="demo-time">New Demo Time:</label>
              <input
                type="datetime-local"
                id="demo-time"
                value={newDemoTime}
                onChange={(e) => setNewDemoTime(e.target.value)}
                disabled={updateLoading}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>

            <button
              type="submit"
              className={styles.rescheduleButton}
              disabled={updateLoading || !newDemoTime}
            >
              {updateLoading ? "Rescheduling..." : "Reschedule Demo"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
